/**
 * Hash an agent id into one of eight specialist identity palette slots. Slots
 * map to CSS variables `--specialist-<name>` / `--specialist-<name>-dark` and
 * are fixed across tenants — the palette is a system-level decision, not a
 * tenant-flexed token. The hash is deterministic so a given agent always
 * renders the same colour, including across page reloads, devices, and tenants.
 */

const SPECIALIST_SLOT_COUNT = 8;

export type SpecialistSlotIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface SpecialistSlot {
  index: SpecialistSlotIndex;
  /** CSS variable name for the slot's primary colour. */
  baseVar: string;
  /** CSS variable name for the slot's darker gradient stop. */
  darkVar: string;
  /** Human-readable slot label (terracotta, olive, ...). */
  name: string;
}

const SLOTS: readonly SpecialistSlot[] = [
  {
    index: 0,
    baseVar: "--specialist-terracotta",
    darkVar: "--specialist-terracotta-dark",
    name: "terracotta",
  },
  { index: 1, baseVar: "--specialist-olive", darkVar: "--specialist-olive-dark", name: "olive" },
  {
    index: 2,
    baseVar: "--specialist-burgundy",
    darkVar: "--specialist-burgundy-dark",
    name: "burgundy",
  },
  { index: 3, baseVar: "--specialist-slate", darkVar: "--specialist-slate-dark", name: "slate" },
  { index: 4, baseVar: "--specialist-gold", darkVar: "--specialist-gold-dark", name: "gold" },
  { index: 5, baseVar: "--specialist-plum", darkVar: "--specialist-plum-dark", name: "plum" },
  { index: 6, baseVar: "--specialist-teal", darkVar: "--specialist-teal-dark", name: "teal" },
  { index: 7, baseVar: "--specialist-clay", darkVar: "--specialist-clay-dark", name: "clay" },
];

function specialistSlotIndex(agentId: string): SpecialistSlotIndex {
  let hash = 0;
  for (let i = 0; i < agentId.length; i += 1) {
    hash = ((hash << 5) - hash + agentId.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % SPECIALIST_SLOT_COUNT) as SpecialistSlotIndex;
}

export function specialistSlot(agentId: string): SpecialistSlot {
  return SLOTS[specialistSlotIndex(agentId)];
}

export function specialistGradient(agentId: string): string {
  const slot = specialistSlot(agentId);
  return `linear-gradient(135deg, var(${slot.baseVar}) 0%, var(${slot.darkVar}) 100%)`;
}
